import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import type { TILE_STYLES } from '@cnt-workspace/ui';
import type * as L from 'leaflet';
import { googleMapsUrl, hasCoordinates, roundCoordinate } from './location-map.util';

type View = 'satellite' | 'streets';

/** Close enough to see a parking lot or a dump station from above. */
const PINNED_ZOOM = 17;

/**
 * The record's location on a map, under an editor's Latitude/Longitude
 * fields, so an admin approving a POI or boondock can see where it is without
 * copying the numbers into Google Maps. Opens on satellite. Dragging the pin
 * or clicking the map moves it and emits `pinMoved` (the editor updates its
 * fields, as an unsaved change); typing new numbers moves the pin.
 */
@Component({
  selector: 'admin-location-map',
  standalone: true,
  template: `
    <div class="admin-location-map">
      <div class="admin-location-map__bar">
        <div class="admin-location-map__views" role="group" aria-label="Map view">
          <button type="button" [attr.aria-pressed]="view === 'satellite'" (click)="show('satellite')">
            Satellite
          </button>
          <button type="button" [attr.aria-pressed]="view === 'streets'" (click)="show('streets')">Map</button>
        </div>
        @if (pinned) {
          <a class="admin-location-map__google" [href]="googleUrl" target="_blank" rel="noopener">
            Open in Google Maps
          </a>
        }
      </div>
      <div #mapEl class="admin-location-map__map" role="region" aria-label="Map of this location"></div>
      @if (!readonly) {
        <p class="admin-location-map__hint">
          @if (pinned) {
            Drag the pin or click the map to move it; the Latitude and Longitude fields follow. Typing in them moves the
            pin.
          } @else {
            No location yet: click the map to drop a pin, or type the Latitude and Longitude.
          }
        </p>
      }
    </div>
  `,
  styles: [
    `
      .admin-location-map {
        grid-column: 1 / -1;
        margin-top: 8px;
      }
      .admin-location-map__bar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 8px;
      }
      .admin-location-map__views {
        display: inline-flex;
        border: 1px solid var(--cnt-line-strong, rgba(34, 34, 34, 0.16));
        border-radius: 9999px;
        overflow: hidden;
      }
      .admin-location-map__views button {
        padding: 6px 14px;
        border: 0;
        background: #fff;
        color: var(--cnt-ink, #222);
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 600;
        cursor: pointer;
      }
      .admin-location-map__views button[aria-pressed='true'] {
        background: var(--cnt-green, #295d42);
        color: #fff;
      }
      .admin-location-map__google {
        color: var(--cnt-green, #295d42);
        font-size: 0.875rem;
        font-weight: 700;
      }
      .admin-location-map__map {
        height: 320px;
        border: 1px solid var(--cnt-line, rgba(34, 34, 34, 0.08));
        border-radius: 1rem;
        overflow: hidden;
        background: #e8e6df;
      }
      .admin-location-map__hint {
        margin: 6px 0 0;
        color: var(--cnt-muted, #707070);
        font-size: 0.8125rem;
      }
    `,
  ],
})
export class AdminLocationMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);

  @Input() lat: number | string | null | undefined;
  @Input() lon: number | string | null | undefined;
  /** Show the spot only: the pin can't be dragged and clicks don't move it. */
  @Input() readonly = false;
  /** A second, grey pin to compare against (e.g. the live record's spot beside a submitted one). */
  @Input() comparePin?: { lat: number; lon: number } | null;
  /** The admin dragged the pin or clicked the map: the new spot, six decimal places. */
  @Output() readonly pinMoved = new EventEmitter<{ lat: number; lon: number }>();

  @ViewChild('mapEl') mapEl?: ElementRef<HTMLElement>;

  view: View = 'satellite';
  private leaflet?: typeof L;
  private tileStyles?: typeof TILE_STYLES;
  private map?: L.Map;
  private tiles?: L.TileLayer;
  private marker?: L.Marker;
  private compareMarker?: L.Marker;
  private destroyed = false;
  /** The spot the pin last moved to itself, so its own emit doesn't re-center the map. */
  private emitted?: { lat: number; lon: number };

  get pinned(): boolean {
    return hasCoordinates(this.lat, this.lon);
  }

  get googleUrl(): string {
    return googleMapsUrl(this.lat, this.lon);
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.mapEl) return;
    // Loaded only when a map is drawn: the shared UI library is large, and every
    // admin page (and test) that imports @cnt-admin/ui would otherwise load it.
    const ui = await import('@cnt-workspace/ui');
    const leaflet = await ui.loadLeaflet();
    // Leaflet arrives asynchronously: the editor may have closed meanwhile.
    if (this.destroyed || !this.mapEl) return;
    this.leaflet = leaflet;
    this.tileStyles = ui.TILE_STYLES;
    const at = this.pinned ? this.spot() : undefined;
    this.map = leaflet.map(this.mapEl.nativeElement, {
      center: at ?? ui.MAP_DEFAULT_CENTER,
      zoom: at ? PINNED_ZOOM : ui.MAP_DEFAULT_ZOOM,
      scrollWheelZoom: false,
    });
    this.show(this.view);
    if (at) this.placePin(at);
    this.placeComparePin();
    if (!this.readonly) this.map.on('click', (e: L.LeafletMouseEvent) => this.moveTo(e.latlng.lat, e.latlng.lng));
    // Ionic sizes its content after first paint; Leaflet measures too early without this.
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  ngOnChanges(): void {
    this.placeComparePin();
    if (!this.map || !this.pinned) return;
    const [lat, lon] = this.spot();
    // Our own drag or click came back through the editor's fields: nothing to move.
    if (this.emitted && this.emitted.lat === lat && this.emitted.lon === lon) return;
    this.placePin([lat, lon]);
    this.map.setView([lat, lon], Math.max(this.map.getZoom(), PINNED_ZOOM - 2));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.map?.remove();
    this.map = undefined;
  }

  /** Switches between satellite imagery and the street map. */
  show(view: View): void {
    this.view = view;
    if (!this.map || !this.leaflet || !this.tileStyles) return;
    const style = this.tileStyles[view];
    this.tiles?.remove();
    this.tiles = this.leaflet.tileLayer(style.url, { attribution: style.attribution, maxZoom: 19 }).addTo(this.map);
  }

  private spot(): [number, number] {
    return [Number(this.lat), Number(this.lon)];
  }

  private moveTo(lat: number, lon: number): void {
    const spot = { lat: roundCoordinate(lat), lon: roundCoordinate(lon) };
    this.placePin([spot.lat, spot.lon]);
    this.emitted = spot;
    // Leaflet events run outside Angular's zone; the editor's fields should update now.
    this.zone.run(() => this.pinMoved.emit(spot));
  }

  /** The grey comparison pin, when there is one; it never moves. */
  private placeComparePin(): void {
    if (!this.map || !this.leaflet) return;
    const c = this.comparePin;
    if (!c || !hasCoordinates(c.lat, c.lon)) {
      this.compareMarker?.remove();
      this.compareMarker = undefined;
      return;
    }
    if (this.compareMarker) {
      this.compareMarker.setLatLng([c.lat, c.lon]);
      return;
    }
    const icon = this.leaflet.divIcon({
      className: 'admin-location-map__compare-pin',
      html: `<div style="width:18px;height:18px;border-radius:50%;background:#707070;border:3px solid #fff;
        box-shadow:0 4px 10px rgba(0,0,0,.3);"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
    this.compareMarker = this.leaflet
      .marker([c.lat, c.lon], { icon, interactive: false, keyboard: false, title: 'Live record' })
      .addTo(this.map);
  }

  private placePin(at: [number, number]): void {
    if (!this.map || !this.leaflet) return;
    if (this.marker) {
      this.marker.setLatLng(at);
      return;
    }
    const icon = this.leaflet.divIcon({
      className: 'admin-location-map__pin',
      html: `<div style="position:relative;width:32px;height:44px;">
        <div style="position:absolute;left:50%;top:0;transform:translateX(-50%);width:28px;height:28px;border-radius:50%;
          background:var(--cnt-trinidad,#e3530d);border:3px solid #fff;box-shadow:0 6px 16px rgba(0,0,0,.3);"></div>
        <div style="position:absolute;left:50%;top:24px;transform:translateX(-50%);width:0;height:0;
          border-left:6px solid transparent;border-right:6px solid transparent;border-top:12px solid var(--cnt-trinidad,#e3530d);"></div>
      </div>`,
      iconSize: [32, 44],
      iconAnchor: [16, 40],
    });
    this.marker = this.leaflet.marker(at, { icon, draggable: !this.readonly, title: 'Location pin' }).addTo(this.map);
    if (this.readonly) return;
    this.marker.on('dragend', () => {
      const ll = this.marker?.getLatLng();
      if (ll) this.moveTo(ll.lat, ll.lng);
    });
  }
}
